/**
 * Preset storage — saves visual params to localStorage and supports
 * JSON import/export so users can move presets between devices.
 *
 * Schema: a single keyed namespace `pulsefx-viz.presets.vN`, version-bumped
 * when the shape changes. Each preset stores a `Partial<BioclastParams>`
 * snapshot so brand-new fields default-merge cleanly when an older preset is
 * loaded into a newer build (no silent crashes).
 *
 * Auto-save: the running params are mirrored into a separate
 * `pulsefx-viz.params.v1` key on every change so a refresh never wipes your
 * tuning. The auto-saved params are *not* shown in the preset dropdown.
 */

import { BioclastParams, DEFAULT_BIOCLAST } from "../viz/scenes/bioclast";
import type { SceneType } from "../viz/renderer";
import { SCENES } from "../viz/scenes/scenePresets";

const PRESET_KEY = "pulsefx-viz.presets.v2";
const PARAMS_KEY = "pulsefx-viz.params.v1";
const RUNNING_STATE_KEY = "pulsefx-viz.running.v2";

const KNOWN_SCENE_TYPES = new Set<SceneType>([
  "bioclast",
  ...SCENES.map((scene) => scene.sceneType ?? "bioclast"),
]);

export function isKnownSceneType(value: unknown): value is SceneType {
  return typeof value === "string" && KNOWN_SCENE_TYPES.has(value as SceneType);
}

export interface CreatorVisualState {
  sceneType: SceneType;
  params: BioclastParams;
}

/** Stored shape of a preset on disk (versioned). */
interface StoredPreset {
  name: string;
  /** ISO 8601 timestamp of last save — useful for the UI to show recency. */
  savedAt: string;
  /** Snapshot of the visual params; partial so older presets keep working. */
  params: Partial<BioclastParams>;
  /** Renderer engine required to reproduce the look. Missing means Bioclast. */
  sceneType?: SceneType;
}

/** Lightweight summary surface used by the UI dropdown. */
export interface Preset {
  name: string;
  savedAt: string;
  sceneType: SceneType;
  /** Pre-merged params: defaults + stored values. */
  params: BioclastParams;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Filters incoming params to only the keys defined on `DEFAULT_BIOCLAST`.
 * Drops unknown fields (e.g. params from a future build) so they don't get
 * persisted forever and silently break things.
 */
function sanitizeParams(input: unknown): Partial<BioclastParams> {
  if (!isPlainObject(input)) return {};
  const out: Partial<BioclastParams> = {};
  for (const key of Object.keys(DEFAULT_BIOCLAST) as (keyof BioclastParams)[]) {
    if (key in input) {
      const value = input[key as string];
      const expected = typeof DEFAULT_BIOCLAST[key];
      // Allow exact type match. Booleans must be booleans, numbers numbers,
      // strings (e.g. cameraMode, conceptMode) strings.
      if (typeof value === expected) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (out as Record<string, unknown>)[key as string] = value as any;
      }
    }
  }
  return out;
}

function loadAllStored(): StoredPreset[] {
  try {
    const raw = localStorage.getItem(PRESET_KEY);
    if (!raw) {
      // One-time migration: try to read the v1 schema (just hue + intensity)
      // so users who already saved presets don't lose them.
      return migrateLegacyV1();
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: StoredPreset[] = [];
    for (const item of parsed) {
      if (!isPlainObject(item)) continue;
      const name = item.name;
      if (typeof name !== "string" || !name) continue;
      const savedAt =
        typeof item.savedAt === "string" ? item.savedAt : new Date().toISOString();
      const params = sanitizeParams(item.params);
      const sceneType = isKnownSceneType(item.sceneType)
        ? item.sceneType
        : "bioclast";
      out.push({ name, savedAt, params, sceneType });
    }
    return out;
  } catch {
    return [];
  }
}

function migrateLegacyV1(): StoredPreset[] {
  try {
    const raw = localStorage.getItem("pulsefx-viz.presets.v1");
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const migrated: StoredPreset[] = [];
    for (const item of parsed) {
      if (!isPlainObject(item)) continue;
      const name = item.name;
      if (typeof name !== "string" || !name) continue;
      const params: Partial<BioclastParams> = {};
      if (typeof item.paletteHue === "number") {
        params.paletteHue = item.paletteHue;
      }
      if (typeof item.intensity === "number") {
        params.intensity = item.intensity;
      }
      migrated.push({
        name,
        savedAt: new Date().toISOString(),
        params,
      });
    }
    if (migrated.length > 0) {
      saveAllStored(migrated);
    }
    return migrated;
  } catch {
    return [];
  }
}

function saveAllStored(list: StoredPreset[]): void {
  try {
    localStorage.setItem(PRESET_KEY, JSON.stringify(list));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

function inflate(stored: StoredPreset): Preset {
  return {
    name: stored.name,
    savedAt: stored.savedAt,
    sceneType: stored.sceneType ?? "bioclast",
    params: { ...DEFAULT_BIOCLAST, ...stored.params },
  };
}

export const presets = {
  list(): Preset[] {
    return loadAllStored().map(inflate);
  },
  get(name: string): Preset | undefined {
    const stored = loadAllStored().find((p) => p.name === name);
    return stored ? inflate(stored) : undefined;
  },
  /**
   * Save (or overwrite) a preset. Pass the *full* current params object;
   * we'll snapshot it. Any fields equal to the DEFAULT are still saved so
   * loading the preset feels reproducible.
   */
  upsert(
    name: string,
    params: BioclastParams,
    sceneType: SceneType = "bioclast",
  ): void {
    const list = loadAllStored().filter((p) => p.name !== name);
    list.push({
      name,
      savedAt: new Date().toISOString(),
      params: sanitizeParams(params),
      sceneType,
    });
    saveAllStored(list);
  },
  remove(name: string): void {
    saveAllStored(loadAllStored().filter((p) => p.name !== name));
  },
  exportJson(): string {
    return JSON.stringify(
      { schema: PRESET_KEY, items: loadAllStored() },
      null,
      2,
    );
  },
  importJson(json: string): number {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      return 0;
    }
    // Accept both the new shape ({ schema, items }) and a bare array of
    // presets for forward/back-compat with manual edits.
    let incomingRaw: unknown[] = [];
    if (Array.isArray(parsed)) {
      incomingRaw = parsed;
    } else if (isPlainObject(parsed) && Array.isArray(parsed.items)) {
      incomingRaw = parsed.items;
    } else {
      return 0;
    }
    const incoming: StoredPreset[] = [];
    for (const item of incomingRaw) {
      if (!isPlainObject(item)) continue;
      const name = item.name;
      if (typeof name !== "string" || !name) continue;
      // Old v1 imports stored hue/intensity at the top level.
      const params: Partial<BioclastParams> = isPlainObject(item.params)
        ? sanitizeParams(item.params)
        : sanitizeParams(item);
      incoming.push({
        name,
        savedAt:
          typeof item.savedAt === "string"
            ? item.savedAt
            : new Date().toISOString(),
        params,
        sceneType: isKnownSceneType(item.sceneType)
          ? item.sceneType
          : "bioclast",
      });
    }
    const existing = loadAllStored();
    const byName = new Map<string, StoredPreset>();
    for (const p of [...existing, ...incoming]) byName.set(p.name, p);
    saveAllStored(Array.from(byName.values()));
    return incoming.length;
  },
};

// ---------------------------------------------------------------------------
// Auto-save of running params (separate key — never appears in the preset list)
// ---------------------------------------------------------------------------

/**
 * Loads the last-known running params, default-merged so a missing field
 * (added in a newer build) lands on its current default rather than crashing
 * the renderer.
 */
export function loadRunningParams(): BioclastParams {
  try {
    const raw = localStorage.getItem(PARAMS_KEY);
    if (!raw) return { ...DEFAULT_BIOCLAST };
    const parsed: unknown = JSON.parse(raw);
    return { ...DEFAULT_BIOCLAST, ...sanitizeParams(parsed) };
  } catch {
    return { ...DEFAULT_BIOCLAST };
  }
}

/** Persists the running params snapshot. Cheap; safe to call on every change. */
export function saveRunningParams(params: BioclastParams): void {
  try {
    localStorage.setItem(PARAMS_KEY, JSON.stringify(sanitizeParams(params)));
  } catch {
    /* quota / private mode */
  }
}

/** Loads scene-aware running state, migrating the legacy params-only key. */
export function loadRunningState(): CreatorVisualState {
  try {
    const raw = localStorage.getItem(RUNNING_STATE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isPlainObject(parsed) && parsed.v === 2) {
        return {
          sceneType: isKnownSceneType(parsed.sceneType)
            ? parsed.sceneType
            : "bioclast",
          params: {
            ...DEFAULT_BIOCLAST,
            ...sanitizeParams(parsed.params),
          },
        };
      }
    }
  } catch {
    /* fall through to legacy migration */
  }
  return { sceneType: "bioclast", params: loadRunningParams() };
}

/** Persists the complete renderer identity required to reproduce a look. */
export function saveRunningState(
  sceneType: SceneType,
  params: BioclastParams,
): void {
  try {
    localStorage.setItem(RUNNING_STATE_KEY, JSON.stringify({
      v: 2,
      sceneType,
      params: sanitizeParams(params),
    }));
  } catch {
    /* quota / private mode */
  }
}

/** Wipes the auto-saved params (used by the panel's "Reset all" button). */
export function clearRunningParams(): void {
  try {
    localStorage.removeItem(PARAMS_KEY);
    localStorage.removeItem(RUNNING_STATE_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Shareable preset URLs (vizz.fm-style ?preset=<base64> deep links)
// ---------------------------------------------------------------------------

/**
 * URL-safe base64 (RFC 4648 §5): replace + / with - _ and strip padding.
 * Keeps the link copy/paste friendly across chat apps that mangle base64.
 */
function b64UrlEncode(s: string): string {
  // btoa needs Latin-1; encodeURIComponent + unescape is the canonical
  // utf-8 → btoa shim that works in every browser (incl. Safari).
  const utf8 = unescape(encodeURIComponent(s));
  return btoa(utf8).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64UrlDecode(s: string): string {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/") +
    "===".slice((s.length + 3) % 4);
  return decodeURIComponent(escape(atob(padded)));
}

/**
 * Encode a preset (just the params snapshot, sanitized) into a URL-safe
 * base64 string suitable for `?preset=<...>`. The shape includes a small
 * header so we can evolve the wire format without breaking old links.
 */
export function encodePresetParam(
  params: BioclastParams,
  name?: string,
  sceneType: SceneType = "bioclast",
): string {
  const payload = {
    v: 2,
    n: name && name.trim() ? name.trim().slice(0, 80) : undefined,
    s: sceneType,
    p: sanitizeParams(params),
  };
  return b64UrlEncode(JSON.stringify(payload));
}

/**
 * Decode a `?preset=` value into a fully-defaulted params object plus the
 * optional preset name. Returns `null` if the payload is malformed or from
 * a future major version we don't understand.
 */
export function decodePresetParam(
  raw: string,
): { name?: string; sceneType: SceneType; params: BioclastParams } | null {
  try {
    const json = b64UrlDecode(raw.trim());
    const parsed: unknown = JSON.parse(json);
    if (!isPlainObject(parsed)) return null;
    if (parsed.v !== 1 && parsed.v !== 2) return null;
    const name = typeof parsed.n === "string" ? parsed.n : undefined;
    const sceneType = parsed.v === 2 && isKnownSceneType(parsed.s)
      ? parsed.s
      : "bioclast";
    const params: BioclastParams = {
      ...DEFAULT_BIOCLAST,
      ...sanitizeParams(parsed.p),
    };
    return { name, sceneType, params };
  } catch {
    return null;
  }
}

/**
 * Build a shareable URL for the current params. Strips any existing
 * `preset` and `ui` query params so the new link is clean.
 */
export function buildShareUrl(
  params: BioclastParams,
  name?: string,
  base?: URL | string,
): string {
  const url = new URL(
    typeof base === "string" ? base : (base?.toString() ?? window.location.href),
  );
  url.searchParams.delete("ui");
  url.searchParams.set("preset", encodePresetParam(params, name));
  return url.toString();
}

/** Build a share URL that preserves both renderer identity and parameters. */
export function buildCreatorShareUrl(
  params: BioclastParams,
  sceneType: SceneType,
  name?: string,
  base?: URL | string,
): string {
  const url = new URL(
    typeof base === "string" ? base : (base?.toString() ?? window.location.href),
  );
  url.searchParams.delete("ui");
  url.searchParams.set("preset", encodePresetParam(params, name, sceneType));
  return url.toString();
}

/**
 * Read `?preset=` from the current page URL (or a supplied URL) and decode
 * it. Returns `null` if absent or invalid.
 */
export function readPresetFromLocation(
  href: string = window.location.href,
): { name?: string; sceneType: SceneType; params: BioclastParams } | null {
  try {
    const url = new URL(href);
    const raw = url.searchParams.get("preset");
    if (!raw) return null;
    return decodePresetParam(raw);
  } catch {
    return null;
  }
}
